<template>
  <div
    v-if="officer.current_status === 'open'"
    class="panel panel-default summaryPanel"
    :id="'officer-' + officer.id"
  >
    <div class="panel-heading">
      <div class="row">
        <div class="col-xs-4">
          <table width="100%">
            <tbody>
              <tr>
                <td>
                  <h1>
                    <i v-if="officer.officertype.name === 'person'" class="fas fa-user"></i>
                    <i v-if="officer.officertype.name === 'company'" class="fas fa-building"></i>
                  </h1>
                </td>
                <td>
                  <template v-if="showDiff && hasDiffConcerningOfficer">
                    <table
                      width="100%"
                      :class="{ 'other-diff-as-well': hasDiffConcerningPosition }"
                    >
                      <tr style="border-bottom: 1px solid #aaaaaa">
                        <th class="diff diff-old text-left">
                          {{ diffConcerningOfficer.previous.date }}
                        </th>
                        <th class="diff diff-new text-left">
                          {{ diffConcerningOfficer.current.date }}
                        </th>
                      </tr>
                      <tr style="height: 30px">
                        <td class="diff diff-old text-left" style="padding-left: 5px">
                          {{ diffConcerningOfficer.previous.full_name }}
                        </td>
                        <td class="diff diff-new text-left" style="padding-left: 5px">
                          {{ diffConcerningOfficer.current.full_name }}
                        </td>
                      </tr>
                    </table>
                  </template>
                  <template v-else>
                    <template v-if="officer.officertype.name === 'person'">
                      <h5>{{ isEmptyMessage(officer.full_name) }}</h5>
                    </template>
                    <template v-else>
                      <h5>{{ isEmptyMessage(officer.company_name) }}</h5>
                    </template>
                  </template>
                  <template v-if="showDiff && hasDiffConcerningPosition">
                    <table width="100%">
                      <tr style="border-bottom: 1px solid #aaaaaa">
                        <th class="diff diff-old text-left" width="50%">
                          {{ diffConcerningPosition.previous_date }}
                        </th>
                        <th class="diff diff-new text-left" width="50%">
                          {{ diffConcerningPosition.current_date }}
                        </th>
                      </tr>
                      <tr
                        v-for="(unused, index) in longestPositionArray"
                        style="height: 30px"
                        class="diff-row"
                        :key="index"
                      >
                        <td
                          class="diff diff-old text-left"
                          valign="top"
                          style="word-break: break-all; text-align: left; padding: 5px"
                        >
                          <template v-if="typeof position('previous', index) === 'object'">
                            <div v-for="(thingy, key) in position('previous', index)" :key="key">
                              <div v-if="keyForDisplay(key) !== undefined">
                                {{ keyForDisplay(key) }}: {{ thingy }}
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            {{ position('previous', index) }}
                          </template>
                        </td>
                        <td
                          class="diff diff-new text-left"
                          valign="top"
                          style="word-break: break-all; text-align: left; padding: 5px"
                        >
                          <template v-if="typeof position('current', index) === 'object'">
                            <div v-for="(thingy, key) in position('current', index)" :key="key">
                              <div v-if="keyForDisplay(key) !== undefined">
                                {{ keyForDisplay(key) }}: {{ thingy }}
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            {{ position('current', index) }}
                          </template>
                        </td>
                      </tr>
                    </table>
                  </template>
                  <template v-else>
                    <template v-if="officer.officertype.name === 'person'">
                      <div class="h7">{{ isEmptyMessage(officer.current_position, '') }}</div>
                      <br />
                    </template>
                    <template v-else>
                      <div>{{ isEmptyMessage(officer.company_registration_number, '') }}</div>
                      <div class="h7">{{ isEmptyMessage(officer.current_position, '') }}</div>
                    </template>
                  </template>
                  <template v-if="officer.officertype.name === 'person'">
                    <div v-if="report.includes_director_reports" style="margin-bottom: 15px">
                      <span class="fas fa-align-left" title="Other directorships"></span
                      >&nbsp;&nbsp;{{ numberOfActiveOtherDirectorships(officer) }} other active
                      directorships
                      <span
                        v-if="numberOfActiveOtherDirectorships(officer) === 50"
                        class="directorship-warning"
                        ><br />&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Maximum nuber of directorships reached
                        (first 50 shown)</span
                      >
                    </div>
                    <p v-if="officer.date_of_birth_month" style="margin-top: 5px">
                      <span class="fas fa-birthday-cake" title="Birthday"></span>&nbsp;&nbsp;
                      {{
                        isEmptyMessage(
                          formatDOB(officer.date_of_birth_month, officer.date_of_birth_year)
                        )
                      }}
                    </p>
                  </template>
                  <template v-else>
                    <div class="h7"></div>
                  </template>

                  <span v-if="officer.is_shareholder" class="label label-neutral">Shareholder</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-xs-8" style="text-align: right">
          <a
            class="accordion-toggle"
            data-toggle="collapse"
            v-bind:href="'#officer' + officer.duedil_officer_id"
          ></a>
        </div>
      </div>
    </div>
    <div class="panel-collapse collapse" v-bind:id="'officer' + officer.duedil_officer_id">
      <div class="panel-body">
        <div class="row">
          <div class="col-md-6">
            <company-directorships :officer="officer" :report="report"></company-directorships>
          </div>
          <div class="col-md-6">
            <other-directorships :officer="officer" :report="report"></other-directorships>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CompanyDirectorships from '@/components/reports/panels/directors/CompanyDirectorships';
import OtherDirectorships from '@/components/reports/panels/directors/OtherDirectorships';
import toolsMixin from '@/components/reports/ToolsMixin';

export default {
  components: {
    CompanyDirectorships,
    OtherDirectorships,
  },
  computed: {
    diffConcerningOfficer() {
      return this.diffData.find(
        (d) =>
          d.type === 'director' &&
          (d.current.officer_id === this.officer.duedil_officer_id ||
            d.previous.officer_id === this.officer.duedil_officer_id)
      );
    },
    diffConcerningPosition() {
      return this.diffData.find(
        (p) => p.type === 'position' && p.officer_id === this.officer.duedil_officer_id
      );
    },
    hasDiffConcerningOfficer() {
      return this.diffConcerningOfficer !== undefined;
    },
    hasDiffConcerningPosition() {
      return this.diffConcerningPosition !== undefined;
    },
    longestPositionArray() {
      const pDiff = this.diffConcerningPosition;
      const result = pDiff.current.length > pDiff.previous.length ? pDiff.current : pDiff.previous;
      return result;
    },
  },
  mixins: [toolsMixin],
  props: ['diffData', 'officer', 'report', 'showDiff'],
  methods: {
    keyForDisplay(key) {
      if (key === 'status') {
        return 'Status';
      }
      if (key === 'officialRole' || key === 'official_role') {
        return 'Official Role';
      }
      if (key === 'selfDescribedPosition' || key === 'position') {
        return 'Position';
      }
      return undefined;
    },
    numberOfActiveOtherDirectorships(officer) {
      let countActive = 0;
      for (let i = 0; i < officer.otherappointments.length; i += 1) {
        if (officer.otherappointments[i].status === 'open') {
          countActive += 1;
        }
      }

      return countActive;
    },
    otherAppointmentsHseCasesCount(officer) {
      const cases = [];
      for (let i = 0; i < officer.otherappointments.length; i += 1) {
        if (officer.otherappointments[i].hsecases.length !== 0) {
          cases.push(officer.otherappointments[i].hsecases);
        }
      }
      return cases.length;
    },
    position(currentOrPrevious, idx) {
      const pDiff = this.diffConcerningPosition;
      if (pDiff[currentOrPrevious][idx] !== undefined) {
        return pDiff[currentOrPrevious][idx];
      }
      return '[ Position no longer used ]';
    },
  },
};
</script>

<style scoped>
.directorship-warning {
  color: #e0741c !important;
  font-size: 80%;
}
.other-diff-as-well {
  margin-bottom: 10px;
}
</style>
